<script lang="ts">
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import StatisticNumberCard from "@kontextwerk/web-sdk/ui/charts/components/StatisticNumberCard.svelte"
  import { mdiDatabaseImportOutline, mdiRefresh } from "@mdi/js"
  import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import type { OrgaViewFrameContext } from "../../../../context"
  import {
    createDistrictStatisticsScope,
    type DistrictStatisticsScope,
    type DistrictStatisticsState,
  } from "./statistics-scope"
  import CustomerAiImportOverlay from "./_modules/customer-ai-import-overlay"
  import { customerImportLocked } from "../../../../../_shared/_logic/customer-import-status"
  import {
    CUSTOMER_REGISTRY_ACTION_TOOLTIP_CLASS,
    customerRegistryActionTooltips,
  } from "../../_shared/_logic/customer-registry-action-tooltips"
  import { invalidateCustomerRegistryTables } from "../../_shared/_logic/customer-registry-invalidation"
  import { buildDistrictStatisticsCards } from "./_logic/presentation"

  const { viewFrameContext } = $props<{ viewFrameContext: OrgaViewFrameContext }>()
  const organization = $derived(
    !viewFrameContext.organizationId ||
      viewFrameContext.organization?.id === viewFrameContext.organizationId
      ? viewFrameContext.organization
      : null,
  )
  const organizationId = $derived(organization?.id ?? null)
  const canImport = $derived(
    viewFrameContext.currentUserRole === "admin" ||
      viewFrameContext.currentUserRole === "customer_admin",
  )
  const importDisabled = $derived(
    String(organization?.tier ?? "")
      .trim()
      .toLowerCase() === "free_trial",
  )
  const importLocked = $derived(Boolean($customerImportLocked))
  let state = $state<DistrictStatisticsState | null>(null)
  let scope: DistrictStatisticsScope | null = null
  const currentState = $derived(state?.organizationId === organizationId ? state : null)
  const statistics = $derived(currentState?.statistics ?? null)
  const loading = $derived(Boolean(organizationId && (!currentState || currentState.loading)))
  const error = $derived(currentState?.error ?? null)
  const cards = $derived(statistics ? buildDistrictStatisticsCards(statistics) : [])
  const eventOwnerId = $props.id()

  // Bind the external request/subscription lifecycle only to the resolved organization ID.
  $effect(() => {
    const id = organizationId

    if (!id) return

    const currentScope = createDistrictStatisticsScope(id, eventOwnerId, (next) => {
      state = next
    })
    scope = currentScope

    return () => {
      currentScope.dispose()
      scope = null
    }
  })

  const retryRead = () => {
    if (viewFrameContext.error) {
      viewFrameContext.onRetry?.()

      return
    }

    void scope?.refresh()
  }

  const openImport = () => {
    const target = organization

    if (!target?.id || !canImport || importDisabled || importLocked) return

    openOverlay({
      title: "Kehrbezirksimport",
      content: CustomerAiImportOverlay,
      bodyPadding: "none",
      closeOnParentOpen: true,
      properties: {
        organizationId: target.id,
        organizationSlug: target.slug ?? null,
        organizationName: target.name ?? null,
        onCommitted: () => invalidateCustomerRegistryTables(target.id),
      },
    })
  }
</script>

<SectionTitle
  title="Kehrbezirk"
  brightBackground={true}
  styleOuter="margin-bottom: 1.2rem;"
  helpModalTextId="customerRegistry"
>
  {#snippet buttonSnippet()}
    {#if canImport}
      <CTA
        type="secondaryButton"
        content="Kehrbezirksimport"
        ariaLabel="Kehrbezirksimport"
        tooltipContent={importDisabled
          ? customerRegistryActionTooltips.customerImportFreeTrial
          : importLocked
            ? customerRegistryActionTooltips.importLocked
            : customerRegistryActionTooltips.customerImport}
        tooltipClassName={CUSTOMER_REGISTRY_ACTION_TOOLTIP_CLASS}
        onClick={openImport}
        disabled={!organizationId || importDisabled || importLocked}
      >
        {#snippet iconSnippet()}
          <Icon path={mdiDatabaseImportOutline} />
        {/snippet}
      </CTA>
    {/if}
  {/snippet}
</SectionTitle>

{#if error || viewFrameContext.error}
  <div class="statistics-error">
    <p role="alert">{error ?? viewFrameContext.error}</p>
    <CTA
      type="secondaryButton"
      content="Erneut versuchen"
      ariaLabel="Erneut versuchen"
      onClick={retryRead}
    >
      {#snippet iconSnippet()}<Icon path={mdiRefresh} />{/snippet}
    </CTA>
  </div>
{:else if loading || viewFrameContext.loading}
  <p role="status">Kehrbezirksstatistik wird geladen …</p>
{:else if !organizationId}
  <p role="status">Keine Organisation ausgewählt.</p>
{:else}
  <div class="statistics-grid">
    {#each cards as card (card.id)}
      <div class="statistics-card" role="group" aria-label={card.title}>
        <StatisticNumberCard
          title={card.title}
          description={card.description}
          value={card.value}
          importantNumbers={card.importantNumbers}
        />
      </div>
    {/each}
  </div>
{/if}

<style lang="less">
  .statistics-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1rem;
  }

  .statistics-card {
    min-width: 0;
  }

  .statistics-error {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
  }

  @media (max-width: 1100px) {
    .statistics-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
